// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import InfoBubblePointFeature from './InfoBubblePointFeature';
import { IntlProvider } from 'react-intl';
import messages, { locale } from '../../languages/languageConfig';
import LocationAnalysisItem from '../../objects/LocationAnalysisItem';
import { dealAreaFields, ringFromPolygonParam } from '../../helpers/Util';
import LocationAnalysisItemOrigin from '../../enums/LocationAnalysisItemOrigin';

/**
 * Paginated popup that shows deal info (page 1) and facility info (page 2)
 * when a deal pin overlaps with a facility on the map.
 */
class InfoBubbleDealFacility extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            currentPage: 0, // 0 = deal, 1 = facility
        };
    }

    openMoreInfo = () => {
        const { bus, dealFeature, onOpenPointInfo } = this.props;
        const properties = dealFeature.properties;

        bus.emit('TOGGLE_INSIGHT_PANEL', { newValue: true });

        const analysisTypeId = properties.profile === 'driving' ? 'DRIVING_TIME' : 'RADIUS';
        const selection = properties.selection ? JSON.parse(properties.selection) : null;

        const newLocationAnalysisItem = new LocationAnalysisItem({
            type: properties.pointType,
            id: properties.facilityId,
            value: properties.title,
            point: {
                lng:
                    properties.originalLng != null
                        ? properties.originalLng
                        : dealFeature.geometry.coordinates[0],
                lat:
                    properties.originalLat != null
                        ? properties.originalLat
                        : dealFeature.geometry.coordinates[1],
            },
            itemOrigin: LocationAnalysisItemOrigin.LIBRARY_LAYER_FEATURE,
            analysisTypeId,
            selection,
            dealId: properties.id,
            listId: properties.listId,
            // A stored shape rides along on any deal, so it can be switched back to.
            ...(properties.polygon ? { ring: ringFromPolygonParam(properties.polygon) } : {}),
            ...(properties.profile === 'polygon' ? dealAreaFields(properties) : {}),
        });

        const visualReportProps = {
            report: 'market-profile',
            pointType: properties.pointType,
            facilityId: properties.facilityId,
        };

        onOpenPointInfo(newLocationAnalysisItem, visualReportProps);
    };

    render() {
        const {
            dealTitle,
            dealFeature,
            coordinates,
            onOpenPointInfo,
            facilityFeature,
            popupFields,
            locationAnalysisItem,
            onSelectFacility,
            suggestEditFormClickBack,
            hasInternalToolsEnabled,
            bus,
            userPreferences,
        } = this.props;

        const { currentPage } = this.state;
        const totalPages = 2;

        const pagination = (
            <div className="flex-it center space-between geojson-point-popup__poi">
                <div className="current-item">
                    {currentPage + 1}/{totalPages}
                </div>
                <div className="flex-it center">
                    <button
                        disabled={currentPage === 0}
                        className="btn btn-pagination"
                        onClick={() => this.setState({ currentPage: 0 })}
                    >
                        <i className="material-icons">keyboard_arrow_left</i>
                    </button>
                    <button
                        disabled={currentPage === totalPages - 1}
                        className="btn btn-pagination"
                        onClick={() => this.setState({ currentPage: 1 })}
                    >
                        <i className="material-icons">keyboard_arrow_right</i>
                    </button>
                </div>
            </div>
        );

        if (currentPage === 0) {
            const properties = dealFeature.properties || {};
            return (
                <div className="geojson-point-popup geojson-point-popup__deal">
                    <div className="geojson-point-popup__header selectable-text">
                        <span className="geojson-point-popup__title">DEAL</span>
                        <span className="geojson-point-popup__tag">
                            {properties.pointType === 'ss-construction'
                                ? 'DEVELOPMENT SITE'
                                : 'SELF STORAGE'}
                        </span>
                    </div>
                    <div className="position-relative">
                        <div className="geojson-point-popup__subheader">
                            <h5 className="geojson-point-popup__ss-facility-title selectable-text">
                                {dealTitle}
                            </h5>
                        </div>
                        <div className="geojson-point-popup__deal-google-maps">
                            <div className="open-in-google-maps__geojson selectable-text">
                                <a
                                    className="google-map-link md-18"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`https://www.google.com/maps/search/?api=1&query=${[...coordinates]
                                        .reverse()
                                        .join(',')}`}
                                >
                                    Open in Google Maps
                                </a>
                            </div>
                        </div>
                        <div className="flex-it center geojson-point-popup__footer space-between nav">
                            <button onClick={this.openMoreInfo} className="btn btn-details">
                                View more details
                            </button>
                            {pagination}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <IntlProvider locale={locale} messages={messages}>
                <InfoBubblePointFeature
                    popupFields={popupFields}
                    locationAnalysisItem={locationAnalysisItem}
                    features={[facilityFeature]}
                    onSelect={onSelectFacility}
                    onOpenPointInfo={onOpenPointInfo}
                    suggestEditFormClickBack={suggestEditFormClickBack}
                    hasInternalToolsEnabled={hasInternalToolsEnabled}
                    bus={bus}
                    userPreferences={userPreferences}
                    paginationOverride={pagination}
                    dealFeature={dealFeature}
                />
            </IntlProvider>
        );
    }
}

InfoBubbleDealFacility.propTypes = {
    dealTitle: PropTypes.string.isRequired,
    dealFeature: PropTypes.object.isRequired,
    coordinates: PropTypes.arrayOf(PropTypes.number).isRequired,
    onCloseDeal: PropTypes.func,
    onOpenPointInfo: PropTypes.func,
    facilityFeature: PropTypes.object.isRequired,
    popupFields: PropTypes.object.isRequired,
    locationAnalysisItem: PropTypes.object,
    onSelectFacility: PropTypes.func,
    suggestEditFormClickBack: PropTypes.func,
    hasInternalToolsEnabled: PropTypes.bool,
    bus: PropTypes.object.isRequired,
    userPreferences: PropTypes.object,
};

export default InfoBubbleDealFacility;
